import type { Layout } from '../../data/types';
import type { Vec2, Ring, Segment } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import { createPolygonIndex } from '../../geo/polygonIndex';
import { createSpatialHash } from '../../geo/spatialHash';
import { centroid, distancePointSegment, pointInRing, ringBounds } from '../../geo/polygon';
import { landmarkObstacleRings } from '../fabric/landmarks/landmarkObstacles';
import { collectFortificationReserved } from '../fabric/fortifications/reserved';
import { layoutWaterTest } from '../fabric/fortifications/moat';
import { lotFrame } from '../fabric/lotFrame';
import type { PlannedLots } from '../fabric/fabricLots';
import type { LifePose, LifeRole, LifeRoute } from './types';
import { routinePoseAt } from './routinePose';

export const HUMAN_RADIUS = 0.48;
// A circle encloses the full turning horse, not merely the width of its chest.
export const HORSE_RADIUS = 1.7;
const TURN_SECONDS = 3;
const STEP = 0.5;

export interface LifeClearance {
  clear(position: Vec2, radius: number): boolean;
}

/** Body clearance follows actual grounded components, preserving the open civic streets. */
export function lifeObstacleRings(
  layout: Layout,
  lots: PlannedLots,
  site: SiteModel
): readonly Ring[] {
  const forts = layout.fortifications;
  return [
    ...[...lots.houses, ...lots.backhouses].map((l) => l.corners),
    // Conservatively keep off the front and corner stoepen, including houses whose sampled
    // shop/door keeps a step. These envelopes use the same maximum reaches as stoep.ts.
    ...lots.houses.map((lot) => {
      const frame = lotFrame(lot);
      const street = layout.streets.find((s) => s.id === lot.sourceId);
      const reach = Math.min(street?.kind === 'quay' ? 1.15 : 0.85, (street?.width ?? 6) * 0.2);
      const at = (depth: number, side: number): Vec2 => [
        frame.frontMid[0] + frame.facing[0] * depth + frame.right[0] * side,
        frame.frontMid[1] + frame.facing[1] * depth + frame.right[1] * side,
      ];
      const half = frame.width / 2 + 1.15;
      return [
        at(-Math.min(frame.depth, 3.5), -half),
        at(reach, -half),
        at(reach, half),
        at(-Math.min(frame.depth, 3.5), half),
      ] as Ring;
    }),
    ...lots.gangen.flatMap((g) => g.kamers.map((k) => k.corners)),
    ...layout.landmarks.flatMap(landmarkObstacleRings),
    ...collectFortificationReserved(
      forts.walls,
      forts.towers,
      forts.gates,
      layoutWaterTest(layout.water)
    ).map((r) => r.polygon.outer),
    // Keep this first cast away from raised bridge components and water stair landings.
    ...site.bridges.flatMap((b) => b.deck.flatMap((d) => [d.outer, ...d.holes])),
    ...(site.waterAccessDecks ?? []),
  ];
}

/** Hospital courts are open working ground; the ward and furniture envelopes still stop bodies. */
export function openSquares(layout: Layout): readonly Ring[] {
  return [
    ...layout.squares.map((square) => square.polygon as Ring),
    ...layout.landmarks
      .filter((landmark) =>
        ['landmark-sint-pietersgasthuis', 'landmark-leprozenhuis'].includes(landmark.id)
      )
      .map((landmark) => landmark.footprint),
  ].filter((ring) => ring.length >= 3);
}

function nearestOn(p: Vec2, { a, b }: Segment): Vec2 {
  const ab: Vec2 = [b[0] - a[0], b[1] - a[1]],
    lengthSq = ab[0] * ab[0] + ab[1] * ab[1];
  if (lengthSq < 1e-10) return a;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * ab[0] + (p[1] - a[1]) * ab[1]) / lengthSq));
  return [a[0] + ab[0] * t, a[1] + ab[1] * t];
}

/**
 * Checks the swept actor's full circle, rather than trusting one dry centre point. The site keeps
 * its streets apart from its squares, so the squares are given with them: without the Dam nobody
 * could stand before the town hall, and every route went round it.
 */
export function createLifeClearance(
  site: SiteModel,
  obstacles: readonly Ring[],
  squares: readonly Ring[] = []
): LifeClearance {
  const hash = createSpatialHash<Ring>(16);
  for (const ring of obstacles) hash.insert(ring, ringBounds(ring));
  const streets = createPolygonIndex(site.streets, 16);
  const open = squares.map((ring) => ({ ring, bounds: ringBounds(ring) }));
  const walkable = (p: Vec2): boolean =>
    streets.contains(p) || open.some(({ ring }) => pointInRing(p, ring));
  /* Where a street runs out into a square the line between them is open ground on both sides. An
     edge bounds the walkable ground only where what lies just past it is neither. */
  const hems = (p: Vec2, edge: Segment, reach: number): boolean => {
    const at = nearestOn(p, edge),
      gap = Math.hypot(at[0] - p[0], at[1] - p[1]);
    if (gap >= reach) return false;
    if (gap < 1e-6) return true;
    return !walkable([
      at[0] + ((at[0] - p[0]) / gap) * 0.08,
      at[1] + ((at[1] - p[1]) / gap) * 0.08,
    ]);
  };
  return {
    clear(p, radius) {
      const reach = radius + 0.15;
      const box = {
        minX: p[0] - reach,
        maxX: p[0] + reach,
        minY: p[1] - reach,
        maxY: p[1] + reach,
      };
      if (
        box.minX < site.bounds.minX ||
        box.maxX > site.bounds.maxX ||
        box.minY < site.bounds.minY ||
        box.maxY > site.bounds.maxY
      )
        return false;
      if (site.waterIndex.contains(p) || !walkable(p)) return false;
      if (site.waterIndex.edgesNear(box).some((edge) => distancePointSegment(p, edge) < reach))
        return false;
      // No path may stray from its actual paved street into a house plot or unmodelled verge.
      if (streets.edgesNear(box).some((edge) => hems(p, edge, reach))) return false;
      for (const { ring, bounds } of open) {
        if (
          box.maxX < bounds.minX ||
          box.minX > bounds.maxX ||
          box.maxY < bounds.minY ||
          box.minY > bounds.maxY
        )
          continue;
        if (ring.some((a, i) => hems(p, { a, b: ring[(i + 1) % ring.length]! }, reach)))
          return false;
      }
      return hash
        .query(box)
        .every(
          (ring) =>
            !pointInRing(p, ring) &&
            ring.every(
              (a, i) => distancePointSegment(p, { a, b: ring[(i + 1) % ring.length]! }) >= reach
            )
        );
    },
  };
}

interface Request {
  readonly role: LifeRole;
  readonly landmark: string;
  readonly count: number;
  readonly reach: number;
}

// A handful of invented everyday routes, not a census or named historical portrait cast.
const REQUESTS: readonly Request[] = [
  { role: 'burgomaster', landmark: 'landmark-oude-stadhuis', count: 1, reach: 80 },
  { role: 'friar', landmark: 'landmark-minderbroeders', count: 2, reach: 95 },
  { role: 'sister', landmark: 'landmark-sint-lucien', count: 2, reach: 90 },
  { role: 'gentleperson', landmark: 'landmark-oude-stadhuis', count: 2, reach: 120 },
  { role: 'shipmaster', landmark: 'landmark-nieuwe-brug-east-headhouse', count: 1, reach: 85 },
  { role: 'sailor', landmark: 'landmark-nieuwe-brug-west-tower', count: 3, reach: 150 },
  { role: 'horse', landmark: 'gate-sint-antoniespoort', count: 1, reach: 150 },
];

function interpolate(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

export function routeClear(
  route: Pick<LifeRoute, 'a' | 'b' | 'radius'>,
  clearance: LifeClearance
): boolean {
  return clearSweep(route, clearance, STEP);
}

/**
 * Frame-sized sweeps need their actual spacing so a clear body can move past a column.
 */
export function stepClear(
  route: Pick<LifeRoute, 'a' | 'b' | 'radius'>,
  clearance: LifeClearance
): boolean {
  return clearSweep(
    route,
    clearance,
    Math.min(STEP, Math.hypot(route.b[0] - route.a[0], route.b[1] - route.a[1]))
  );
}

function clearSweep(
  route: Pick<LifeRoute, 'a' | 'b' | 'radius'>,
  clearance: LifeClearance,
  spacing: number
): boolean {
  const length = Math.hypot(route.b[0] - route.a[0], route.b[1] - route.a[1]);
  const count = spacing ? Math.ceil(length / spacing) : 0;
  for (let i = 0; i <= count; i++)
    if (
      !clearance.clear(
        interpolate(route.a, route.b, count ? i / count : 0),
        route.radius + spacing / 2
      )
    )
      return false;
  return true;
}

function separated(a: Segment, b: Segment, distance: number): boolean {
  // Accepted routes are straight. Sampling plus a half-step margin also rejects crossings.
  const n = Math.max(1, Math.ceil(Math.hypot(a.b[0] - a.a[0], a.b[1] - a.a[1]) / STEP));
  return Array.from({ length: n + 1 }, (_, i) => interpolate(a.a, a.b, i / n)).every(
    (p) => distancePointSegment(p, b) > distance + STEP / 2
  );
}

/** Short returns on existing streets; missing or blocked routes omit an actor and report it. */
export function planLifeRoutes(
  layout: Layout,
  clearance: LifeClearance,
  warn: (message: string) => void = () => undefined
): readonly LifeRoute[] {
  const routes: LifeRoute[] = [];
  for (const request of REQUESTS) {
    const anchor = layout.landmarks.find((l) => l.id === request.landmark);
    const gate = layout.fortifications.gates.find((g) => g.id === request.landmark);
    if (!anchor && !gate) {
      warn(`${request.role}: no ${request.landmark} route anchor`);
      continue;
    }
    const at = anchor ? centroid(anchor.footprint) : gate!.pos;
    const radius = request.role === 'horse' ? HORSE_RADIUS : HUMAN_RADIUS;
    const candidates = layout.streets
      .flatMap((street) =>
        street.centerline.slice(0, -1).flatMap((a, i) => {
          const b = street.centerline[i + 1]!;
          const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
          if (length < 8 || street.width < 2 * (radius + 0.4)) return [];
          // No shifted route centre can approach its anchor more than the 1.2 m lane offset.
          // Reject distant segments before allocating every five-metre candidate along them.
          if (distancePointSegment(at, { a, b }) > request.reach + 1.2) return [];
          const direction: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
          const lane = request.role === 'horse' ? 0 : Math.min(1.2, street.width * 0.14);
          const size = request.role === 'horse' ? 18 : 12;
          return [0, lane, -lane].flatMap((offset) => {
            const shift = (p: Vec2): Vec2 => [
              p[0] - direction[1] * offset,
              p[1] + direction[0] * offset,
            ];
            const n = Math.max(1, Math.ceil(length / 5));
            return Array.from({ length: n }, (_, j) => {
              const start = (j * length) / n;
              const end = Math.min(length, start + size);
              const from = shift(interpolate(a, b, start / length));
              const to = shift(interpolate(a, b, end / length));
              const middle = interpolate(from, to, 0.5);
              return {
                streetId: street.id,
                a: from,
                b: to,
                distance: Math.hypot(middle[0] - at[0], middle[1] - at[1]),
                length: end - start,
              };
            });
          });
        })
      )
      .filter((c) => c.length >= 7 && c.distance <= request.reach)
      .sort((a, b) => a.distance - b.distance);
    let found = 0;
    for (const candidate of candidates) {
      if (found >= request.count) break;
      const route: LifeRoute = {
        id: `${request.role}-${found}`,
        role: request.role,
        streetId: candidate.streetId,
        a: candidate.a,
        b: candidate.b,
        radius,
        speed:
          request.role === 'horse'
            ? 0.8
            : request.role === 'burgomaster'
              ? 0.65
              : request.role === 'friar' || request.role === 'sister'
                ? 0.7
                : 0.85,
        scale: request.role === 'horse' ? 1 : 0.96 + (routes.length % 3) * 0.035,
        phase: routes.length * 4.731,
      };
      if (!routeClear(route, clearance)) continue;
      if (
        routes.some((existing) => !separated(route, existing, route.radius + existing.radius + 0.5))
      )
        continue;
      routes.push(route);
      found++;
    }
    if (found !== request.count)
      warn(`${request.role}: ${found}/${request.count} clear street routes`);
  }
  return routes;
}

/** A paused half-turn at each end makes a continuous presentation without teleporting. */
export function lifePoseAt(route: LifeRoute, timeSeconds: number): LifePose {
  if (route.routine?.length) return routinePoseAt(route, timeSeconds);
  const length = Math.hypot(route.b[0] - route.a[0], route.b[1] - route.a[1]);
  const travel = length / route.speed,
    half = travel + TURN_SECONDS,
    period = half * 2;
  const time = (((timeSeconds + route.phase) % period) + period) % period;
  const returnLeg = time >= half;
  const local = time % half;
  const walking = local < travel;
  const t = walking ? local / travel : 1;
  const from = returnLeg ? route.b : route.a,
    to = returnLeg ? route.a : route.b;
  const heading = Math.atan2(route.b[1] - route.a[1], route.b[0] - route.a[0]);
  const turn = walking ? 0 : (local - travel) / TURN_SECONDS;
  const smoothTurn = turn * turn * (3 - 2 * turn);
  const ramp = Math.min(1, local / 0.7, (travel - local) / 0.7);
  const gaitWeight = walking ? Math.max(0, ramp) : 0;
  const gait = (local * route.speed * Math.PI * 2) / 1.2;
  return {
    position: interpolate(from, to, t),
    heading: heading + (returnLeg ? Math.PI : 0) + Math.PI * smoothTurn,
    stride: Math.sin(gait) * gaitWeight,
    gait,
    gaitWeight,
    walking,
  };
}
